import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/* =========================================================
   SITE STATE
   ========================================================= */

export interface SiteState {
  siteId: string;

  siteName: string;

  ccPhone: string;

  detailLead: string;

  detailLeadPhone: string;

  timezone: string;

  shiftStartHours: string;

  shiftStartMinutes: string;

  siteStreet: string;

  siteState: string;

  siteCity: string;

  siteZip: string;
}

/* =========================================================
   INITIAL STATE
   ========================================================= */

const createInitialState = (): SiteState => ({
  siteId: '',

  siteName: '',

  ccPhone: '',

  detailLead: '',

  detailLeadPhone: '',

  timezone: '',

  shiftStartHours: '',

  shiftStartMinutes: '',

  siteStreet: '',

  siteState: '',

  siteCity: '',

  siteZip: '',
});

const initialState = createInitialState();

/* =========================================================
   SLICE
   ========================================================= */

const siteSlice = createSlice({
  name: 'site',

  initialState,

  reducers: {
    // -----------------------------------------------------
    // SITE
    // -----------------------------------------------------

    setSiteId(state, action: PayloadAction<string>) {
      state.siteId = action.payload;
    },

    setSiteName(state, action: PayloadAction<string>) {
      state.siteName = action.payload;
    },

    setCcPhone(state, action: PayloadAction<string>) {
      state.ccPhone = action.payload;
    },

    setDetailLead(state, action: PayloadAction<string>) {
      state.detailLead = action.payload;
    },

    setDetailLeadPhone(state, action: PayloadAction<string>) {
      state.detailLeadPhone = action.payload;
    },

    setTimezone(state, action: PayloadAction<string>) {
      state.timezone = action.payload;
    },

    setShiftStartHours(state, action: PayloadAction<string>) {
      state.shiftStartHours = action.payload;
    },

    setShiftStartMinutes(state, action: PayloadAction<string>) {
      state.shiftStartMinutes = action.payload;
    },

    setSiteStreet(state, action: PayloadAction<string>) {
      state.siteStreet = action.payload;
    },

    setSiteState(state, action: PayloadAction<string>) {
      state.siteState = action.payload;
    },

    setSiteCity(state, action: PayloadAction<string>) {
      state.siteCity = action.payload;
    },

    setSiteZip(state, action: PayloadAction<string>) {
      state.siteZip = action.payload;
    },

    // =====================================================
    // EXISTING SITE -> FORM
    // =====================================================

    hydrateSiteForm(state, action: PayloadAction<any>) {
      const site = action.payload;

      state.siteId = site?._id ?? site?.id ?? '';

      state.siteName = site?.siteName ?? '';

      state.ccPhone = site?.sitePhone ?? site?.ccPhone ?? '';

      state.detailLead = site?.siteLead ?? site?.detailLead ?? '';

      state.detailLeadPhone =
        site?.siteLeadPhone ?? site?.detailLeadPhone ?? '';

      state.timezone = site?.timezone ?? '';

      // -----------------------------------------
      // SHIFT START
      // -----------------------------------------

      if (site?.shiftStartTime && typeof site.shiftStartTime === 'string') {
        const [hour = '', minute = ''] = site.shiftStartTime.split(':');

        state.shiftStartHours = hour;

        state.shiftStartMinutes = minute;
      } else {
        state.shiftStartHours = site?.shiftStartHours ?? '';

        state.shiftStartMinutes = site?.shiftStartMinutes ?? '';
      }

      // -----------------------------------------
      // ADDRESS
      // -----------------------------------------

      state.siteStreet = site?.siteStreet ?? '';

      state.siteState = site?.siteState ?? '';

      state.siteCity = site?.siteCity ?? '';

      state.siteZip = site?.siteZip ?? '';
    },

    // =====================================================
    // RESET
    // =====================================================

    resetForm() {
      return createInitialState();
    },
  },
});

/* =========================================================
   EXPORT
   ========================================================= */

export const {
  setSiteId,

  setSiteName,

  setCcPhone,

  setDetailLead,

  setDetailLeadPhone,

  setTimezone,

  setShiftStartHours,

  setShiftStartMinutes,

  setSiteStreet,

  setSiteState,

  setSiteCity,

  setSiteZip,

  hydrateSiteForm,

  resetForm,
} = siteSlice.actions;

export default siteSlice.reducer;
